// src/components/TenantsPage.js
import React, { useEffect, useState, useCallback } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import useRole from "../hooks/useRole";

export default function TenantsPage() {
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = useRole();

  const [tenants, setTenants] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [originalRoom, setOriginalRoom] = useState("");

  const [form, setForm] = useState({
    fullName: "",
    gender: "Male",
    phone: "",
    email: "",
    occupation: "",
    idNumber: "",
    emergencyContact: "",
    emergencyRelation: "",
    assignedRoom: "",
    checkInDate: "",
    status: "Active",
  });

  // LOAD DATA
  const loadData = useCallback(async () => {
    const tSnap = await getDocs(collection(db, "tenants"));
    const rSnap = await getDocs(collection(db, "rooms"));

    const tenantList = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const roomList = rSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    setTenants(tenantList);
    setFilteredTenants(tenantList);
    setRooms(roomList);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // SEARCH FILTER
  useEffect(() => {
    const q = search.toLowerCase();
    setFilteredTenants(
      tenants.filter((t) => {
        const name = t.fullName?.toLowerCase() || "";
        const phone = t.phone?.toLowerCase() || "";
        const room = t.assignedRoom?.toLowerCase() || "";
        return name.includes(q) || phone.includes(q) || room.includes(q);
      })
    );
  }, [search, tenants]);

  // OPEN CREATE
  const openCreate = () => {
    setEditId(null);
    setForm({
      fullName: "",
      gender: "Male",
      phone: "",
      email: "",
      occupation: "",
      idNumber: "",
      emergencyContact: "",
      emergencyRelation: "",
      assignedRoom: "",
      checkInDate: "",
      status: "Active",
    });
    setShowModal(true);
  };

  // OPEN EDIT
  const openEdit = (t) => {
    if (!canEdit) return;
    setEditId(t.id);
    setOriginalRoom(t.assignedRoom);
    setForm(t);
    setShowModal(true);
  };

  // SAVE TENANT
  const saveTenant = async () => {
    if (!form.fullName.trim()) return alert("Full name required");
    if (!form.assignedRoom) return alert("Please select a room");

    if (editId) {
      await updateDoc(doc(db, "tenants", editId), form);
    } else {
      await addDoc(collection(db, "tenants"), form);
    }

    loadData();
    setShowModal(false);
  };

  // DELETE TENANT
  const removeTenant = async (id) => {
    if (!canDelete) return;
    if (!window.confirm("Delete this tenant?")) return;

    await deleteDoc(doc(db, "tenants", id));

    setTenants((prev) => prev.filter((t) => t.id !== id));
    setFilteredTenants((prev) => prev.filter((t) => t.id !== id));
  };

  // RENDER UI
  return (
    <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50 min-h-screen p-5 md:p-10">

      <div className="bg-white/95 rounded-2xl shadow-xl border border-amber-50 p-6 md:p-8">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[#b4642d]">
            Tenants
          </h1>

          {(canAdd || canEdit) && (
            <button
              className="bg-[#b4642d] text-white px-4 py-2 rounded-full hover:bg-[#944d22]"
              onClick={openCreate}
            >
              + Add Tenant
            </button>
          )}
        </div>

        {/* SEARCH */}
        <input
          className="border p-2 w-60 mb-4 rounded"
          placeholder="Search name / phone / room"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* TABLE */}
        <div className="overflow-x-auto border rounded-2xl shadow-xl bg-white">
          <table className="w-full text-sm">
            <thead className="bg-orange-50 text-[#b4642d] font-semibold">
              <tr>
                <th className="p-3 text-left">Tenant Name</th>
                <th className="p-3 text-left">Room</th>
                <th className="p-3 text-left">Phone</th>
                <th className="p-3 text-left">Check-In</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredTenants.map((t) => (
                <tr key={t.id} className="border-b hover:bg-orange-50">
                  <td className="p-3">{t.fullName}</td>
                  <td className="p-3">{t.assignedRoom}</td>
                  <td className="p-3">{t.phone}</td>
                  <td className="p-3">{t.checkInDate || "--"}</td>

                  <td className="p-3">
                    <span
                      className={`px-2 py-1 rounded text-xs text-white ${
                        t.status === "Active"
                          ? "bg-green-600"
                          : "bg-yellow-600"
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>

                  <td className="p-3 text-center space-x-3">
                    {canEdit && (
                      <button
                        className="text-blue-600 hover:underline"
                        onClick={() => openEdit(t)}
                      >
                        Edit
                      </button>
                    )}

                    <button
                      className="text-purple-600 hover:underline"
                      onClick={() => navigate(`/dashboard/tenants/${t.id}`)}
                    >
                      View
                    </button>

                    {canDelete && (
                      <button
                        className="text-red-600 hover:underline"
                        onClick={() => removeTenant(t.id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>

      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white p-6 rounded-xl w-96 max-h-[90vh] shadow-lg overflow-y-auto">
            <h2 className="font-bold text-lg mb-4">
              {editId ? "Edit Tenant" : "New Tenant"}
            </h2>

            {/* FORM FIELDS */}
            <input
              className="border w-full p-2 mb-3 rounded"
              placeholder="Full Name"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />

            <select
              className="border w-full p-2 mb-3 rounded"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
            >
              <option>Male</option>
              <option>Female</option>
            </select>

            <input
              className="border w-full p-2 mb-3 rounded"
              placeholder="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />

            <input
              className="border w-full p-2 mb-3 rounded"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />

            {/* ROOM DROPDOWN */}
            <select
              className="border w-full p-2 mb-3 rounded"
              value={form.assignedRoom}
              onChange={(e) =>
                setForm({ ...form, assignedRoom: e.target.value })
              }
            >
              <option value="">Select Room</option>

              {rooms
                .filter((r) => {
                  if (r.roomNo === form.assignedRoom) return true;
                  return r.status === "Available";
                })
                .map((r) => (
                  <option key={r.id} value={r.roomNo}>
                    {r.roomNo} ({r.status})
                  </option>
                ))}
            </select>

            <input
              type="date"
              className="border w-full p-2 mb-3 rounded"
              value={form.checkInDate}
              onChange={(e) =>
                setForm({ ...form, checkInDate: e.target.value })
              }
            />

            <select
              className="border w-full p-2 mb-3 rounded"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option>Active</option>
              <option>Left</option>
            </select>

            <div className="flex justify-end space-x-3 pt-3">
              <button onClick={() => setShowModal(false)}>Cancel</button>

              {(canAdd || canEdit) && (
                <button
                  className="bg-[#b4642d] text-white px-4 py-1 rounded-full"
                  onClick={saveTenant}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
